import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n';
import { getDedicatedBySlug } from '@/data/dedicatedServers';
import { SEOHead } from '@/components/SEOHead';
import { productSchema, breadcrumbSchema } from '@/lib/seo';
import { Server, Cpu, Network, Shield, Clock, Zap, Tag, ArrowLeft } from 'lucide-react';

export default function DedicatedStandardPage() {
  const { t } = useTranslation();
  const type = getDedicatedBySlug('standard');

  const features = [
    { icon: Shield, text: t('dedicated.feat_ddos') },
    { icon: Clock,  text: t('dedicated.feat_setup_time') },
    { icon: Network, text: t('dedicated.feat_network_1g') },
    { icon: Cpu,    text: t('dedicated.feat_root') },
  ];

  return (
    <Layout>
      <SEOHead
        title={`Standard Dedicated Servers from €${type.fromPrice}`}
        description={`Standard dedicated servers from €${type.fromPrice}/month: Intel Core & AMD Ryzen CPUs, 16–32 GB RAM, SSD/NVMe storage and 1 Gbit/s uplink. DDoS protection, no contract.`}
        canonical="/dedicated/standard"
        schema={[
          productSchema({
            name: 'Standard Dedicated Server',
            description: 'Dedicated servers with Intel Core & AMD Ryzen CPUs, 16–32 GB RAM, SSD/NVMe storage, 1 Gbit/s uplink and DDoS protection.',
            path: '/dedicated/standard',
            offers: type.servers.map((server) => ({ name: server.name, price: server.price, period: 'month' })),
          }),
          breadcrumbSchema([
            { name: 'Dedicated Servers', path: '/dedicated' },
            { name: 'Standard Dedicated Servers', path: '/dedicated/standard' },
          ]),
        ]}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Back */}
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="mb-8">
            <Link
              to="/dedicated"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-dedicated"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('dedicated.backToDedicated')}
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              {t('dedicated.standard_hero1')}{' '}
              <span className="text-gradient">{t('dedicated.standard_hero2')}</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('dedicated.standard_desc')}
            </p>

            {/* Feature Badges */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {features.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 px-4 py-2 rounded-full glass">
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-sm">{text}</span>
                </div>
              ))}
            </div>

            {/* Cross-links */}
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/dedicated/premium">
                <Button variant="outline" className="border-primary/30 hover:border-primary" data-testid="link-to-premium">
                  <Zap className="w-4 h-4 mr-2" />
                  {t('dedicated.viewPremium')}
                </Button>
              </Link>
              <a href="https://deinserverhost.de/dedicated-sonderposten" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-green-500/30 hover:bg-green-500 hover:border-green-500 text-green-400" data-testid="link-to-sale">
                  <Tag className="w-4 h-4 mr-2" />
                  {t('dedicated.viewSale')}
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Server Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="overflow-x-auto mb-16"
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-4 px-4 font-semibold text-muted-foreground">{t('dedicated.col_server')}</th>
                  <th className="text-left py-4 px-4 font-semibold text-muted-foreground">{t('dedicated.col_cpu')}</th>
                  <th className="text-left py-4 px-4 font-semibold text-muted-foreground">{t('dedicated.col_ram')}</th>
                  <th className="text-left py-4 px-4 font-semibold text-muted-foreground">{t('dedicated.col_storage')}</th>
                  <th className="text-left py-4 px-4 font-semibold text-muted-foreground">{t('dedicated.col_network')}</th>
                  <th className="text-left py-4 px-4 font-semibold text-muted-foreground">{t('dedicated.col_setup')}</th>
                  <th className="text-right py-4 px-4 font-semibold text-muted-foreground">{t('dedicated.col_price')}</th>
                  <th className="py-4 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {type.servers.map((server, index) => (
                  <motion.tr
                    key={server.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`border-b border-border hover:bg-white/5 transition-colors ${server.popular ? 'bg-primary/5' : ''}`}
                    data-testid={`dedicated-standard-row-${index}`}
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{server.name}</span>
                        {server.popular && (
                          <Badge className="bg-primary">{t('dedicated.popular')}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-sm">
                        <div className="font-medium">{server.cpu}</div>
                        <div className="text-muted-foreground">{server.cores}</div>
                      </div>
                    </td>
                    <td className="py-4 px-4 font-medium">{server.ram}</td>
                    <td className="py-4 px-4 text-sm">{server.storage}</td>
                    <td className="py-4 px-4">
                      <Badge variant="outline">{server.network}</Badge>
                    </td>
                    <td className="py-4 px-4">
                      {server.setup === '0.00' ? (
                        <span className="text-green-500 font-medium">{t('dedicated.freeSetup')}</span>
                      ) : (
                        <span>€{server.setup}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="text-xl font-bold text-primary">€{server.price}</div>
                      <div className="text-xs text-muted-foreground">{t('dedicated.perMonth')}</div>
                    </td>
                    <td className="py-4 px-4">
                      <a href={server.orderUrl ?? type.orderUrl} target="_blank" rel="noopener noreferrer">
                        <Button
                          size="sm"
                          className="bg-primary hover:bg-primary/90"
                          data-testid={`configure-standard-${index}`}
                        >
                          {t('dedicated.configureNow')}
                        </Button>
                      </a>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center p-8 glass rounded-2xl"
          >
            <h2 className="text-2xl font-bold mb-4">{t('dedicated.standard_ctaTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              {t('dedicated.standard_ctaDesc')}
            </p>
            <Link to="/dedicated/premium">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" data-testid="cta-premium-btn">
                {t('dedicated.standard_ctaBtn')}
              </Button>
            </Link>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
